import {
  CloudWatchLogsClient,
  CreateLogStreamCommand,
  PutLogEventsCommand,
} from '@aws-sdk/client-cloudwatch-logs';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../../aws-exports';

const SHOW_LOCAL_LOGS = false;

const LOG_BATCH_SIZE = 25;
const LOG_FLUSH_INTERVAL = 5000; // 5 seconds
let logQueue: any[] = [];
let flushTimeout: any | null = null;

const flushLogs = async () => {
  if (logQueue.length === 0) return;

  const batchesToSend = [...logQueue];
  logQueue = [];

  if (!cloudwatchClient) return;

  const logGroupName = process.env.NEXT_PUBLIC_CLOUDWATCH_LOG_GROUP;
  const logStreamName = `${process.env.NEXT_PUBLIC_CLOUDWATCH_LOG_STREAM}-${new Date().toISOString().split('T')[0]}`;

  try {
    // Create log stream if it doesn't exist
    const createStreamCommand = new CreateLogStreamCommand({
      logGroupName,
      logStreamName,
    });
    await cloudwatchClient?.send(createStreamCommand);
  } catch (error) {
    // Ignore if stream already exists
    if ((error as Error).name !== 'ResourceAlreadyExistsException') {
      console.log('Failed to create log stream:', error);
      return;
    }
  }

  try {
    await cloudwatchClient.send(
      new PutLogEventsCommand({
        logGroupName,
        logStreamName,
        logEvents: batchesToSend.map((log) => ({
          message: JSON.stringify(log),
          timestamp: log.timestamp || Date.now(),
        })),
      }),
    );
  } catch (error) {
    console.error('Failed to flush logs:', error);
  }
};

Amplify.configure({
  ...awsConfig,
});

let cloudwatchClient: CloudWatchLogsClient | null = null;

const getCloudWatchClient = async (): Promise<CloudWatchLogsClient | null> => {
  try {
    const { credentials } = await fetchAuthSession();

    return new CloudWatchLogsClient({
      region: process.env.NEXT_PUBLIC_AWS_REGION || 'us-west-2',
      credentials: {
        accessKeyId: credentials?.accessKeyId || '',
        secretAccessKey: credentials?.secretAccessKey || '',
        sessionToken: credentials?.sessionToken || '',
      },
    });
  } catch (error) {
    console.warn('Failed to initialize CloudWatch client:', error);
    return null;
  }
};

type LogLevel = 'info' | 'error' | 'warn' | 'debug';

interface LogMessage {
  type: string;
  requestId?: string;
  route?: string;
  url?: string;
  method?: string;
  status?: number;
  headers?: Record<string, any>;
  body?: any;
  error?: string;
  metadata?: Record<string, any>;
  timestamp?: number;
}

const consoleLogger = {
  info: (message: any) =>
    console.log('[INFO]:', JSON.stringify(message, null, 2)),
  error: (message: any) =>
    console.error('[ERROR]:', JSON.stringify(message, null, 2)),
  warn: (message: any) =>
    console.warn('[WARN]:', JSON.stringify(message, null, 2)),
  debug: (message: any) =>
    console.debug('[DEBUG]:', JSON.stringify(message, null, 2)),
};

export const logToCloudWatch = async (
  logMessage: LogMessage,
  logLevel: LogLevel = 'info',
) => {
  if (SHOW_LOCAL_LOGS) {
    consoleLogger[logLevel](logMessage);
  }

  if (process.env.NODE_ENV === 'development') {
    return;
  }

  if (!cloudwatchClient) {
    cloudwatchClient = await getCloudWatchClient();
  }

  logQueue.push({
    level: logLevel,
    ...logMessage,
    timestamp: logMessage.timestamp || Date.now(),
    environment: process.env.NODE_ENV || 'production',
  });

  if (!flushTimeout) {
    flushTimeout = setTimeout(() => {
      flushTimeout = null;
      flushLogs();
    }, LOG_FLUSH_INTERVAL);
  }

  if (logQueue.length >= LOG_BATCH_SIZE) {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
      flushTimeout = null;
    }
    await flushLogs();
  }
};
